// Turning a sketch into something the simulated board can run.
//
// arduino-cli cannot run in a browser, so the sketch goes to a compile service.
// There are two, because the two boards have nothing in common downstream of
// the compiler: the Nano's goes to `../arduino-compiler` (the service furuta's
// editor also uses) and comes back as Intel HEX text, and the ESP32-S3's goes to
// `../../esp32-compiler/arduino-esp32` and comes back as a zip holding an
// application image and a symbol table.
//
// Which one is picked follows from the board the robot was built with, which is
// why `compileSketch` takes it. Everything above here -- the Run button, the
// build panel -- deals in `Compiled` and does not care which service answered.

import JSZip from "jszip";

// Which host is used is decided at build time, not by a runtime check.
// `__LOCAL_COMPILER__` is rspack's, and is true for anything `rspack serve`
// puts up -- `npm start` and `npm run prod` alike, since running a compile
// service locally has nothing to do with which mode the bundle was built in.
// Only `npm run build`, the bundle that goes into ../docs, points at Cloud Run.
// The literal is substituted in, so the branch not taken never ships.
const AVR_COMPILER_URL = __LOCAL_COMPILER__
  ? "http://localhost:8477"
  : "https://arduino-compiler-spl73nlieq-uc.a.run.app";
const AVR_FQBN = "arduino:avr:nano";

// The ESP32 service, and the FQBN it has to be asked for explicitly: it defaults
// to a plain ESP32, which is a different core with a different instruction set
// and would not boot on the S3 the robot is running.
//
// This is the ESP-IDF 5.x service (`esp32:esp32@3.3.10`), not the v2.0.12 one
// the other sims point at. Booting what it produces needed real work in esp32rs
// -- see the ESP-IDF 5.5 notes in CLAUDE.md -- and the firmware baked into the
// wasm is built from the same image, so the two agree.
//
// Locally it is 8081 rather than the 8080 its README suggests: the rspack dev
// server has no port set and so takes 8080 itself, and the page and the service
// it calls cannot both have it. Run it with `-p 8081:8080`. (The AVR service's
// 8477 is an odd port for the same reason.)
const ESP32_COMPILER_URL = __LOCAL_COMPILER__
  ? "http://localhost:8081"
  : "https://esp32-compile-api-452188812531.us-central1.run.app";

// The PSRAM settings `psramOptions` offers, as the service's `psram` field
// spells them. A setting missing here is sent as "disabled", which is also what
// a deployment predating the field builds whatever it is sent: FastAPI ignores
// a form field it does not know, so an older service answers with a perfectly
// good image that simply finds no PSRAM.
const ESP32_PSRAM: Record<string, string> = {
  Disabled: "disabled",
  "OPI PSRAM": "opi",
};

// The board names, as `src/plan.rs` spells them. They arrive here off
// `AddedRobot.microcontroller`, i.e. out of that same table.
export const ARDUINO_NANO = "Arduino Nano";
export const ESP32_S3 = "ESP32-S3";
export const XIAO_ESP32_S3 = "XIAO ESP32-S3";

// The FQBN each S3 board is compiled as, and the difference is not cosmetic:
// the generic S3's `Serial` is UART0 on GPIO43/44, the XIAO's is the USB CDC
// console, which is what leaves that pair free for `Serial1`.
// `firmware/esp32s3/build.sh` builds each board's welcome image with the same
// FQBN, so the image a robot boots into and the sketches flashed onto it later
// agree.
type Esp32Board = typeof ESP32_S3 | typeof XIAO_ESP32_S3;
const ESP32_FQBNS: Record<Esp32Board, string> = {
  [ESP32_S3]: "esp32:esp32:esp32s3",
  [XIAO_ESP32_S3]: "esp32:esp32:XIAO_ESP32S3",
};

// Whether a board is one of the S3s, i.e. compiled by the ESP32 service and
// flashed with an application image rather than HEX.
export function isEsp32(board: string | undefined): board is Esp32Board {
  return board !== undefined && Object.hasOwn(ESP32_FQBNS, board);
}

// What the AVR service answers with. Success carries `hex`; a compile failure
// comes back as HTTP 422 with the compiler's own output in `details`.
interface AvrCompileResponse {
  success?: boolean;
  hex?: string;
  stdout?: string;
  size?: number;
  error?: string;
  details?: string;
  returncode?: number;
}

// A compiled sketch, in whichever form its board is flashed with. The tag is
// what `editor.ts` switches on to pick between the two re-flash calls, and it
// tracks the board rather than being inferred from which fields are present.
export type Compiled =
  | { board: typeof ARDUINO_NANO; hex: string; log: string }
  | {
      board: Esp32Board;
      appBin: Uint8Array;
      symbols: string;
      log: string;
    };

// Thrown for anything that leaves us without firmware: the service refusing the
// sketch, the compiler rejecting it, or the request never arriving. The message
// is the compiler's output where there is one, so it lands in the build panel
// as the user expects to read it.
export class CompileError extends Error {}

// `psram` is the robot's PSRAM setting, `undefined` for a board that has none.
export async function compileSketch(
  sketch: string,
  board: string,
  psram?: string,
): Promise<Compiled> {
  if (isEsp32(board)) {
    return compileForEsp32(sketch, board, psram);
  }
  if (board === ARDUINO_NANO) {
    return compileForNano(sketch);
  }
  throw new CompileError(`no compiler for ${board}`);
}

async function compileForNano(sketch: string): Promise<Compiled> {
  let response: Response;
  try {
    response = await fetch(`${AVR_COMPILER_URL}/compile`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sketch, fqbn: AVR_FQBN }),
    });
  } catch (error) {
    // A network-level failure has no response to read: the service is asleep,
    // offline, or blocked. Say so rather than reporting it as a compile error.
    throw new CompileError(
      `could not reach the compile service: ${describe(error)}`,
    );
  }

  // Anything but JSON here means the service itself failed (a proxy error page,
  // a rate limit); show the status rather than a parser complaint.
  let result: AvrCompileResponse;
  try {
    result = (await response.json()) as AvrCompileResponse;
  } catch {
    throw new CompileError(`compile service returned HTTP ${response.status}`);
  }

  if (!response.ok || !result.hex) {
    const detail = result.details ?? result.error ?? `HTTP ${response.status}`;
    throw new CompileError(stripAnsi(detail));
  }

  return {
    board: ARDUINO_NANO,
    hex: result.hex,
    log: stripAnsi(result.stdout ?? ""),
  };
}

// The ESP32 service is shaped differently at every step: multipart in rather
// than JSON, a zip out rather than a field, and its errors come back as a
// FastAPI `detail` carrying the compiler log under `compile_log`.
async function compileForEsp32(
  sketch: string,
  board: Esp32Board,
  psram: string | undefined,
): Promise<Compiled> {
  const form = new FormData();
  // The service derives the sketch folder's name from this filename and rejects
  // anything outside `[A-Za-z0-9_.-]`, so it is a fixed safe name rather than
  // whichever of the drop's sketches happens to be on screen.
  form.set(
    "ino_file",
    new Blob([sketch], { type: "text/plain" }),
    "sketch.ino",
  );
  form.set("fqbn", ESP32_FQBNS[board]);
  form.set("psram", ESP32_PSRAM[psram ?? ""] ?? "disabled");

  let response: Response;
  try {
    response = await fetch(`${ESP32_COMPILER_URL}/compile`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      body: form,
    });
  } catch (error) {
    throw new CompileError(
      `could not reach the compile service: ${describe(error)}`,
    );
  }

  if (!response.ok) {
    throw new CompileError(stripAnsi(await esp32Failure(response)));
  }

  const { appBin, symbols, log } = await readCompileZip(
    await response.arrayBuffer(),
  );
  checkChip(appBin);
  return { board, appBin, symbols, log: stripAnsi(log) };
}

// The chip an application image was built for, out of its header.
//
// An ESP-IDF image starts with a magic byte and carries the chip it targets in
// a little-endian half-word twelve bytes in. Both are checked before the image
// is handed to the emulator.
//
// This exists because of the way the service fails when it does not understand
// `fqbn`: FastAPI ignores an unexpected form field rather than rejecting it, so
// a deployment predating that parameter answers HTTP 200 with a perfectly good
// image -- for a plain ESP32, which is a different core with a different
// instruction set. Booting an LX6 image on the emulated LX7 does not fail
// anywhere near here; it runs garbage until it lands on a self-loop and takes
// wasm down with an "infinite loop back to self" from inside esp32rs. There is
// nothing in that message to connect it to a compile service two repos away,
// which is the whole reason for reading two bytes here.
const IMAGE_MAGIC = 0xe9;
const CHIP_IDS: Record<number, string> = {
  0x0000: "ESP32",
  0x0002: "ESP32-S2",
  0x0005: "ESP32-C3",
  0x0009: "ESP32-S3",
  0x000c: "ESP32-C2",
};
const CHIP_ESP32_S3 = 0x0009;

function checkChip(appBin: Uint8Array) {
  const header = new DataView(
    appBin.buffer,
    appBin.byteOffset,
    appBin.byteLength,
  );
  if (appBin.length < 14 || header.getUint8(0) !== IMAGE_MAGIC) {
    throw new CompileError(
      "the compile service returned something that is not an ESP-IDF " +
        "application image",
    );
  }
  const chip = header.getUint16(12, true);
  if (chip !== CHIP_ESP32_S3) {
    throw new CompileError(
      `the compile service built this for an ${CHIP_IDS[chip] ?? `unknown chip (0x${chip.toString(16)})`}, ` +
        `not an ${ESP32_S3}. It is most likely a deployment that predates the ` +
        `\`fqbn\` parameter and ignored the one asked for, in which case it needs ` +
        `rebuilding and redeploying from ../esp32-compiler/arduino-esp32.`,
    );
  }
}

// What went wrong, out of the service's error body. A compile failure is a 400
// whose `detail` is an object with the compiler's output in `compile_log`; a
// rejected request (an unsupported FQBN, a file too large) has a plain string
// there instead.
async function esp32Failure(response: Response): Promise<string> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return `compile service returned HTTP ${response.status}`;
  }
  const detail = (body as { detail?: unknown })?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (detail && typeof detail === "object") {
    const { compile_log: compileLog, message } = detail as {
      compile_log?: string;
      message?: string;
    };
    if (compileLog) {
      return compileLog;
    }
    if (message) {
      return message;
    }
  }
  return `compile service returned HTTP ${response.status}`;
}

// The service's answer is a zip of `*.ino.bin`, `symbols.txt` and `compile.log`.
//
// JSZip rather than a hand-rolled reader: the same dependency, used the same
// way, as the client in `../../esp32-compiler/examples/call-api.ts` and the
// sibling sims that call this service. A zip parser is not something worth
// owning here to save one dependency.
//
// Imported statically, like everything else here. It was a dynamic import once,
// to keep it out of the bundle for a Nano robot that will never unzip anything
// -- but that made it the only async chunk in the app, and rspack's `output`
// sets `filename` without a `chunkFilename` or `publicPath` to match, so the
// chunk 404s at runtime. Not worth fixing the config for: `monaco-editor` is
// statically imported a few files over and is orders of magnitude larger, so
// splitting this one was never buying much.
async function readCompileZip(bytes: ArrayBuffer): Promise<{
  appBin: Uint8Array;
  symbols: string;
  log: string;
}> {
  let zip: Awaited<ReturnType<typeof JSZip.loadAsync>>;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch (error) {
    throw new CompileError(
      `compile service returned something that is not a zip: ${describe(error)}`,
    );
  }

  // Matched by suffix: the image is named after the sketch, which the service
  // takes from the filename posted above.
  const image = Object.values(zip.files).find(
    (entry) => !entry.dir && entry.name.endsWith(".ino.bin"),
  );
  const symbols = zip.file("symbols.txt");
  if (!image) {
    throw new CompileError("the compile service returned no application image");
  }
  if (!symbols) {
    throw new CompileError("the compile service returned no symbol table");
  }

  return {
    appBin: await image.async("uint8array"),
    symbols: await symbols.async("string"),
    // The log is what the build panel shows on success, and its absence is not
    // worth failing a build that otherwise worked.
    log: (await zip.file("compile.log")?.async("string")) ?? "",
  };
}

// arduino-cli colours its output, and the build panel is plain text. Drop the
// escape sequences rather than pulling in a converter for the few lines of
// size reporting a successful build produces.
function stripAnsi(text: string): string {
  // The pattern is a literal ESC followed by `[ ... m`.
  return text.replace(/\[[0-9;]*m/g, "");
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
